/**
 * UniversalSyncToast — single floating widget that renders every
 * in-flight sync job from the unified ``SyncStore`` (May 25, 2026).
 *
 * Mirrors V1's ``components/shared/UniversalSyncToast.tsx`` — the
 * cross-domain progress widget that replaced the per-feature
 * ``EmailSyncToast`` / ``ChatSyncToast`` / ``FilesystemSyncToast``
 * fragmentation. One mount point at the authed-shell root; the store
 * subscription handles everything (email / chat / drive / filesystem
 * jobs are rendered identically).
 *
 * UX rules (copied 1:1 from V1):
 *   - bottom-right floating stack, max-w 360, gap 8px between jobs
 *   - per job: provider chip + label, progress bar, current item,
 *     counters, cancel button, dismiss button on terminal status
 *   - terminal jobs auto-dismiss after 8s; user can dismiss earlier
 *   - ``live=false`` (polling fallback) shows a subtle "in attesa…"
 *     pulse so the user knows the SSE stream dropped
 *
 * No props — the toast is a pure store subscriber.
 */

import { msg } from "@lingui/core/macro";
import type { I18n, MessageDescriptor } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import { Loader2, CheckCircle2, AlertTriangle, X } from "lucide-react";
import * as React from "react";

import { cn } from "@ando/ui";

import {
  type SyncJob,
  useAllSyncJobs,
  useSyncStore,
} from "@/features/sync/state/syncStore";

const AUTO_DISMISS_MS = 8_000;

export const UniversalSyncToast: React.FC = () => {
  const jobs = useAllSyncJobs();

  if (jobs.length === 0) return null;

  // Aggregate counters for the multi-job footer (V1 parity — shows
  // "X in corso · Y completate" once we have more than one job to
  // glance at, so the user doesn't have to count rows).
  const inProgress = jobs.filter(
    (j) =>
      j.status === "pending" ||
      j.status === "processing" ||
      j.status === "running" ||
      j.status === "cancelling",
  ).length;
  const completed = jobs.filter((j) => j.status === "completed").length;
  const failed = jobs.filter((j) => j.status === "failed").length;

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className={cn(
        "pointer-events-none fixed right-2 bottom-2 z-[1000] flex flex-col-reverse gap-2",
        // Mobile: leave 8px breathing room from the viewport edges
        // (iPhone SE 375px would otherwise overflow the 360px panel).
        // sm+ (≥640px) restores the historical 16px gutter and the
        // capped width that V1 also used.
        "max-w-[calc(100vw-1rem)] sm:right-4 sm:bottom-4 sm:max-w-[360px]",
      )}
    >
      {jobs.length > 1 ? (
        <div className="pointer-events-auto self-end rounded-full border border-hairline bg-card/95 px-3 py-1 text-[11px] text-ink-3 tabular-nums shadow-md backdrop-blur-md">
          {inProgress > 0 ? (
            <span className="text-ando-orange">
              <Trans>{inProgress} in corso</Trans>
            </span>
          ) : null}
          {inProgress > 0 && (completed > 0 || failed > 0) ? (
            <span className="mx-1.5 text-ink-3/50">·</span>
          ) : null}
          {completed > 0 ? (
            <span className="text-success">
              <Trans>{completed} completate</Trans>
            </span>
          ) : null}
          {completed > 0 && failed > 0 ? (
            <span className="mx-1.5 text-ink-3/50">·</span>
          ) : null}
          {failed > 0 ? (
            <span className="text-rose-500">
              <Trans>{failed} errori</Trans>
            </span>
          ) : null}
        </div>
      ) : null}
      {jobs.map((job) => (
        <SyncToastRow key={job.jobId} job={job} />
      ))}
    </div>
  );
};
UniversalSyncToast.displayName = "UniversalSyncToast";

// ──────────────────────────────────────────────────────────────────────
// Per-job row
// ──────────────────────────────────────────────────────────────────────

const SyncToastRow: React.FC<{ job: SyncJob }> = ({ job }) => {
  const { i18n } = useLingui();
  const dismissJob = useSyncStore((s) => s.dismissJob);
  const cancelJob = useSyncStore((s) => s.cancelJob);
  const isCancelling = useSyncStore((s) => s.cancelling.has(job.jobId));

  // Auto-dismiss terminal rows after a beat so the user has time to read
  // the final state. Aborted on unmount / before timer fires.
  React.useEffect(() => {
    if (
      job.status !== "completed" &&
      job.status !== "failed" &&
      job.status !== "cancelled"
    ) {
      return;
    }
    const id = window.setTimeout(() => dismissJob(job.jobId), AUTO_DISMISS_MS);
    return () => window.clearTimeout(id);
  }, [job.status, job.jobId, dismissJob]);

  const tone = toneFor(job.status);
  const isActive =
    job.status === "pending" ||
    job.status === "processing" ||
    job.status === "running" ||
    job.status === "cancelling";

  return (
    <div
      className={cn(
        "pointer-events-auto overflow-hidden rounded-lg border bg-card/95 shadow-lg backdrop-blur-md",
        tone.border,
      )}
      role="status"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 pt-2.5 pb-1.5">
        <span
          className={cn("inline-flex h-5 w-5 items-center justify-center", tone.icon)}
        >
          {job.status === "completed" ? (
            <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
          ) : job.status === "failed" ? (
            <AlertTriangle className="h-4 w-4" strokeWidth={2} />
          ) : (
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-[12.5px] text-ink-1">
            {primaryLabel(job, i18n)}
          </p>
          {/* ``title`` exposes the full filename / subject when the
              CSS ``truncate`` clipping kicks in — without it the user
              would never see the full ``20260430_S_template_dati_orari…``
              name (a real Fabio case). The native browser tooltip is
              ARIA-compliant and i18n-free. */}
          <p
            className="truncate text-[11px] text-ink-3"
            title={secondaryLabel(job)}
          >
            {secondaryLabel(job)}
          </p>
        </div>
        <button
          type="button"
          aria-label={i18n._(msg`Chiudi`)}
          onClick={() => dismissJob(job.jobId)}
          className={cn(
            "inline-flex h-6 w-6 items-center justify-center rounded-md",
            "text-ink-3 transition-colors hover:bg-black/[0.04] hover:text-ink-1",
          )}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Progress bar */}
      {isActive ? (
        <div className="px-3 pb-1.5">
          <div className="h-1 w-full overflow-hidden rounded-full bg-ink-3/15">
            <div
              className={cn("h-full transition-[width] duration-500", tone.bar)}
              style={{ width: `${Math.max(2, job.progress)}%` }}
            />
          </div>
        </div>
      ) : null}

      {/* Counters + cancel */}
      <div className="flex items-center justify-between gap-2 px-3 pt-1 pb-2.5">
        <p className="truncate text-[11px] text-ink-3 tabular-nums">
          {countersLine(job, i18n)}
        </p>
        {isActive ? (
          <button
            type="button"
            disabled={isCancelling || job.status === "cancelling"}
            onClick={() => void cancelJob(job.jobId)}
            className={cn(
              "shrink-0 rounded-md px-2 py-0.5 font-medium text-[11px] transition-colors",
              "text-ink-2 hover:bg-black/[0.04] hover:text-ink-1",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            {isCancelling || job.status === "cancelling" ? (
              <Trans>Annullando…</Trans>
            ) : (
              <Trans>Annulla</Trans>
            )}
          </button>
        ) : null}
      </div>

      {/* Polling-fallback subtle hint */}
      {!job.live && isActive ? (
        <div className="border-hairline border-t bg-ink-3/[0.04] px-3 py-1">
          <p className="text-[10.5px] text-ink-3">
            <span className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-ink-3/60" />
            <Trans>Connessione live persa — fallback a polling</Trans>
          </p>
        </div>
      ) : null}
    </div>
  );
};
SyncToastRow.displayName = "SyncToastRow";

// ──────────────────────────────────────────────────────────────────────
// Copy + style helpers
// ──────────────────────────────────────────────────────────────────────

interface Tone {
  readonly border: string;
  readonly icon: string;
  readonly bar: string;
}

function toneFor(status: SyncJob["status"]): Tone {
  if (status === "completed") {
    return {
      border: "border-success/40",
      icon: "text-success",
      bar: "bg-success",
    };
  }
  if (status === "failed") {
    return {
      border: "border-rose-400/50",
      icon: "text-rose-500",
      bar: "bg-rose-400",
    };
  }
  if (status === "cancelled" || status === "cancelling") {
    return {
      border: "border-ink-3/40",
      icon: "text-ink-3",
      bar: "bg-ink-3",
    };
  }
  return {
    border: "border-ando-orange/40",
    icon: "text-ando-orange",
    bar: "bg-ando-orange",
  };
}

const SOURCE_LABEL: Readonly<Record<SyncJob["source"], MessageDescriptor>> = {
  email: msg`Sincronizzazione email`,
  chat: msg`Sincronizzazione chat`,
  docs: msg`Sincronizzazione documenti`,
  filesystem: msg`Sincronizzazione cartella`,
  other: msg`Sincronizzazione`,
};

function primaryLabel(job: SyncJob, i18n: I18n): string {
  // Jun 24, 2026 — distinguish manual uploads from the rest of the
  // ``docs`` source family. Without this branch the user who just
  // clicked "Carica file" saw "Sincronizzazione documenti", which
  // sounds like a background Drive sync — same UI signal for two
  // very different intents. Provider tag is set by
  // ``trackIngestionToast`` so the discrimination is FE-owned.
  const base =
    job.source === "docs" && job.identifier.provider === "manual_upload"
      ? i18n._(msg`Caricamento documento`)
      : i18n._(SOURCE_LABEL[job.source]);
  if (job.status === "completed") return i18n._(msg`${base} · completato`);
  if (job.status === "failed") return i18n._(msg`${base} · errore`);
  if (job.status === "cancelled") return i18n._(msg`${base} · annullato`);
  if (job.status === "cancelling") return i18n._(msg`${base} · annullamento…`);
  return base;
}

// Jun 30, 2026 — traduzione phase BE → label utente. Il BE emette
// nomi macchina inglesi (``discovering``, ``ingesting``, ``finalizing``,
// ``done``) — mostrarli grezzi nel toast confondeva l'utente
// ("ingesting? in che senso?"). Mapping italiano + fallback al raw
// se phase ignota (nuova phase BE non ancora mappata = visibile
// come hint, mai stringa vuota).
const PHASE_LABEL: Readonly<Record<string, string>> = {
  pending: "in coda",
  discovering: "ricerca file",
  discovered: "pronto",
  ingesting: "elaborazione",
  finalizing: "finalizzazione",
  done: "completato",
};

function phaseLabel(phase: string | undefined): string | null {
  if (!phase) return null;
  return PHASE_LABEL[phase] ?? phase;
}

function secondaryLabel(job: SyncJob): string {
  const id = job.identifier;
  const target =
    id.email_address ?? id.connection_key ?? id.connector_id ?? id.provider ?? "";
  // Jun 30, 2026 (review): mostrare PHASE oltre al target invece di
  // sceglierne uno. Il toast aveva 4-5 righe vuote di contesto e l'
  // utente vedeva solo "andrea@…" + "0/4" — phase ("elaborazione",
  // "ricerca file") è il segnale principale di "che sta succedendo
  // ora". File corrente, se presente, sostituisce ENTRAMBI (è il
  // signal più ricco).
  if (job.currentItem) return job.currentItem;
  const phase = phaseLabel(job.phase);
  const parts = [target, phase].filter((s): s is string => Boolean(s));
  return parts.join(" · ") || job.status;
}

function countersLine(job: SyncJob, i18n: I18n): string {
  const parts: string[] = [];
  if (job.totalItems > 0) {
    parts.push(`${job.completedItems}/${job.totalItems}`);
  } else if (job.completedItems > 0) {
    parts.push(String(job.completedItems));
  }
  if (job.failedItems > 0) parts.push(i18n._(msg`${job.failedItems} errori`));
  if (job.skippedItems > 0) parts.push(i18n._(msg`${job.skippedItems} saltati`));
  // Jun 30, 2026 (review-2): mostra `%` SEMPRE quando ``totalItems > 0``
  // (anche 0% all'avvio sync). Senza, l'utente non sa che esiste una %
  // finché un file non completa — pessimo per sync lente. Skip solo a
  // 100% (il counter "N/N" è già esplicito + il toast presto si chiude).
  if (job.totalItems > 0 && job.progress < 100) {
    parts.push(`${job.progress}%`);
  } else if (parts.length === 0 && job.progress > 0) {
    parts.push(`${job.progress}%`);
  }
  return parts.join(" · ") || i18n._(msg`in corso…`);
}
